// File: /frontend/src/components/GooglePlacesAutocomplete.js

import React, { useState, useEffect, useRef } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../config/googleMaps';

/**
 * GooglePlacesAutocomplete
 *
 * PRIMARY:  Google Places autocomplete — suggests addresses and extracts
 *           coordinates when a suggestion is picked.
 * FALLBACK: If the Google Maps API key is missing / invalid / unauthorized,
 *           or the script fails to load, the field silently degrades into a
 *           plain text input so the user can ALWAYS type the address manually.
 *           The typed value still flows to the parent via onChange (coordinates
 *           are simply left unset, and the parent defaults them to [0,0]).
 *
 * The input is never permanently disabled just because Maps failed — that was
 * the old bug that blocked seller registration whenever the key stopped working.
 */
const GooglePlacesAutocomplete = ({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Enter shop address',
  className = '',
  disabled = false,
  error = null
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [status, setStatus] = useState('Loading address suggestions…');
  const [isReady, setIsReady] = useState(false);
  // When true, Maps is unavailable → user types the address manually.
  const [fallbackMode, setFallbackMode] = useState(false);

  useEffect(() => {
    const API_KEY = GOOGLE_MAPS_API_KEY;

    console.log('🚀 Starting Google Places initialization (React)...');
    console.log('🔑 API Key:', API_KEY ? `${API_KEY.substring(0, 10)}...` : 'NOT FOUND');
    console.log('🌐 Environment:', process.env.NODE_ENV);

    // Google calls this global when the key is invalid / not authorized /
    // billing disabled / referer not allowed / API not enabled. When it fires
    // we switch to manual entry instead of leaving a dead field.
    window.gm_authFailure = () => {
      console.error('❌ Google Maps auth failure (invalid key / API not enabled / billing / referer). Falling back to manual entry.');
      setFallbackMode(true);
      setIsReady(false);
      setStatus('✍️ Address suggestions unavailable — please type your full shop address manually.');
    };

    // No key at all → straight to manual entry (still fully usable).
    if (!API_KEY || API_KEY.trim() === '') {
      console.warn('⚠️ No Google Maps API key found — enabling manual address entry.');
      setFallbackMode(true);
      setStatus('✍️ Please type your full shop address (area, city, pincode).');
      return;
    }

    const enableFallback = (reason) => {
      console.error(`❌ Google Maps unavailable (${reason}) — enabling manual address entry.`);
      setFallbackMode(true);
      setIsReady(false);
      setStatus('✍️ Address suggestions unavailable — please type your full shop address manually.');
    };

    // Initialize Google Places autocomplete on the existing input element.
    const initializePlaces = () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          throw new Error('Places library not loaded');
        }

        console.log('⚙️ Creating autocomplete instance...');
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'IN' },
          fields: ['formatted_address', 'geometry', 'name']
        });

        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          console.log('🎉 Place selected:', place);

          if (place.geometry) {
            const placeData = {
              address: place.formatted_address,
              coordinates: [
                place.geometry.location.lng(),
                place.geometry.location.lat()
              ],
              name: place.name
            };

            setStatus(`✅ Selected: ${place.formatted_address}`);

            if (onChange) onChange(place.formatted_address);
            if (onPlaceSelected) onPlaceSelected(placeData);
          } else {
            // User typed something Google didn't resolve to a place — keep it,
            // it's still a valid manually-entered address.
            setStatus('✍️ Using the address you typed.');
          }
        });

        setFallbackMode(false);
        setIsReady(true);
        setStatus('🎯 Ready! Start typing your shop address…');
        console.log('✅ Places autocomplete initialized successfully');
      } catch (err) {
        console.error('❌ Error initializing Places:', err);
        enableFallback(err.message);
      }
    };

    const loadGoogleMaps = () => {
      // Already loaded → init immediately.
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('✅ Google Maps already loaded, initializing...');
        initializePlaces();
        return;
      }

      // Script already in the DOM (another instance is loading it) → wait for it.
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        console.log('📡 Google Maps script already in DOM, waiting for load...');
        setStatus('📡 Loading address suggestions…');

        const checkInterval = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkInterval);
            initializePlaces();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.google || !window.google.maps || !window.google.maps.places) {
            enableFallback('load timeout (existing script)');
          }
        }, 8000);

        return;
      }

      console.log('📡 Loading Google Maps API...');
      setStatus('📡 Loading address suggestions…');

      const callbackName = `initGooglePlaces_${Date.now()}`;
      window[callbackName] = () => {
        console.log('🎯 Google Maps API callback executed');
        delete window[callbackName];
        initializePlaces();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&loading=async&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;

      script.onerror = (err) => {
        console.error('❌ Script loading failed:', err);
        delete window[callbackName];
        enableFallback('script network error');
      };

      document.head.appendChild(script);

      // Safety net: if neither the callback nor gm_authFailure fires in time,
      // fall back to manual entry so the field never stays stuck loading.
      setTimeout(() => {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          enableFallback('load timeout');
        }
      }, 10000);
    };

    loadGoogleMaps();

    return () => {
      if (autocompleteRef.current && window.google) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (err) {
          console.error('Error during cleanup:', err);
        }
      }
      if (window.gm_authFailure) {
        window.gm_authFailure = undefined;
      }
    };
  }, []); // run once

  const handleInputChange = (e) => {
    if (onChange) onChange(e.target.value);
  };

  const isError = status.includes('❌');
  const isManual = fallbackMode;
  const isLoading = !isReady && !fallbackMode && !isError;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={isManual ? 'Type your full shop address (area, city, pincode)' : placeholder}
        // Only disabled if the parent explicitly disables it. Never blocked by
        // Maps loading/failure — manual typing must always work.
        disabled={disabled}
        className={`
          ${className}
          ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
        `}
        autoComplete="off"
      />

      {/* Status Indicator */}
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
        {isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
        )}

        {isReady && (
          <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )}

        {isManual && (
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )}
      </div>

      {/* Status Message — only surface actionable states (error / manual entry).
          The success "🎯 Ready! Start typing…" / loading chatter is intentionally
          hidden: the field is just a clean address box that also accepts manual typing. */}
      {(isError || isManual) && (
        <p className={`mt-1 text-xs ${isError ? 'text-red-600' : 'text-gray-500'}`}>
          {status}
        </p>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
